import dayjs from 'dayjs';
import { stringify } from 'qs';
import { ReplicationPullHandlerResult } from 'rxdb';

import { Api, TSyncBookingsResponse } from '@mopla/data-models';

import {
	IPassengerBookingCheckpoint,
	PASSENGER_BOOKING_SYNC_VERSION,
	TPassengerBookingDocType,
} from '../../schemas/passengerBooking';

export const passengerBookingPullHandler = async (
	api: Api,
	checkpointOrUndefined: IPassengerBookingCheckpoint | undefined
): Promise<
	ReplicationPullHandlerResult<
		TPassengerBookingDocType,
		IPassengerBookingCheckpoint
	>
> => {
	const rawUpdateTimestamp = checkpointOrUndefined?.updateTimestamp ?? null;
	const bookingId = checkpointOrUndefined?.bookingId ?? null;

	/** If the stored checkpoint is in the future (e.g. after a server clock advance in testing),
	 *  reset it to null to trigger a full re-sync and recover correct pagination. */
	const isFutureCheckpoint = Boolean(
		rawUpdateTimestamp && dayjs(rawUpdateTimestamp).isAfter(dayjs())
	);

	/** BE re-sends a booking only when its `updatedTime` moves, so fields added since the last
	 *  sync arrive only via a full pull. */
	const isOutdatedSyncVersion =
		checkpointOrUndefined?.syncVersion !== PASSENGER_BOOKING_SYNC_VERSION;

	// const shouldResetCheckpoint = isFutureCheckpoint;
	const shouldResetCheckpoint = isFutureCheckpoint || isOutdatedSyncVersion;

	const query = stringify(
		{
			bookingId: shouldResetCheckpoint ? null : bookingId,
			updateTimestamp: shouldResetCheckpoint ? null : rawUpdateTimestamp,
		},
		{ skipNulls: true, addQueryPrefix: true }
	);

	const response = await api.get<TSyncBookingsResponse>(
		`/api/passengers/syncBookings${query}`
	);

	const documents = response.content;

	/** Use the last non-future document as the checkpoint anchor.
	 *  Future-dated documents (created during server clock advances in testing) are skipped
	 *  to prevent the checkpoint from drifting into the future and missing real bookings. */
	const now = dayjs();
	const lastRealDocument =
		[...documents].reverse().find((d) => dayjs(d.updatedTime).isBefore(now)) ||
		null;

	const checkpoint: IPassengerBookingCheckpoint | null = lastRealDocument
		? {
				bookingId: lastRealDocument.id,
				updateTimestamp: lastRealDocument.updatedTime,
				syncVersion: PASSENGER_BOOKING_SYNC_VERSION,
		  }
		: checkpointOrUndefined || null;

	return {
		documents: documents as any,
		checkpoint,
	};
};
